import React from "react";
import { Link } from "react-router-dom";
import { PageHero } from "../components/PageHero";
import { Button } from "../components/primitives";
import { useConsent } from "../cookies/ConsentContext";
import { useLang } from "../i18n";
import { COOKIE_INVENTORY, COOKIE_CATEGORIES, siteConfig, orComingSoon } from "../config/siteConfig";

const content = {
  en: {
    over: "Legal", title: "Cookie Policy",
    effective: "Effective date",
    manageBtn: "Manage cookie preferences",
    sections: [
      { h: "What are cookies?", p: "Cookies are small text files (and similar technologies such as local storage) placed on your device when you visit a website. They help the site function, remember your preferences and, where enabled, measure usage." },
      { h: "How WANM uses cookies", p: "WANM uses a minimal set of strictly necessary cookies to run the website, keep it secure, handle forms and remember your consent choice. Optional analytics and marketing technologies are only used if you consent and only once they have been configured." },
      { h: "Types of cookies", list: [
        "Strictly necessary — always active; required for the site to work.",
        "Analytics — optional; measures how the site is used (e.g. Google Analytics). Not active until configured and consented.",
        "Marketing / advertising — optional; reserved for future advertising, conversion and affiliate tracking (e.g. Meta Pixel). Not active until configured and consented.",
      ] },
      { h: "Third-party services", p: "WANM does not currently load third-party analytics or advertising cookies. When such integrations are activated in the future, they will be listed in the cookie inventory below and will only run according to your consent." },
      { h: "Currently active cookies", table: true },
      { h: "How to manage cookies", p: "You can accept or reject non-essential cookies at any time using the button below or the \"Cookie Preferences\" link in the footer. You can also control cookies through your browser settings." },
      { h: "Cookie retention / duration", p: "Retention periods are listed per cookie in the inventory above. Your consent preference is stored for up to 6 months, after which you will be asked again." },
      { h: "Updates to this Cookie Policy", p: "We may update this policy as our services and integrations evolve. Material changes will be reflected here with a revised effective date." },
      { h: "Contact", p: "For privacy or cookie questions, contact us at:" },
    ],
    invHead: ["Name", "Provider", "Purpose", "Category", "Duration", "Party"],
  },
  es: {
    over: "Legal", title: "Política de Cookies",
    effective: "Fecha de entrada en vigor",
    manageBtn: "Gestionar preferencias de cookies",
    sections: [
      { h: "¿Qué son las cookies?", p: "Las cookies son pequeños archivos de texto (y tecnologías similares como el almacenamiento local) que se colocan en tu dispositivo al visitar un sitio web. Ayudan a que el sitio funcione, recuerdan tus preferencias y, cuando están habilitadas, miden el uso." },
      { h: "Cómo utiliza WANM las cookies", p: "WANM utiliza un conjunto mínimo de cookies estrictamente necesarias para operar el sitio, mantenerlo seguro, gestionar formularios y recordar tu elección de consentimiento. Las tecnologías opcionales de analítica y marketing solo se usan si das tu consentimiento y una vez que han sido configuradas." },
      { h: "Tipos de cookies", list: [
        "Estrictamente necesarias — siempre activas; necesarias para el funcionamiento del sitio.",
        "Analítica — opcional; mide cómo se usa el sitio (p. ej. Google Analytics). No se activa hasta configurarla y consentirla.",
        "Marketing / publicidad — opcional; reservada para futuro seguimiento de publicidad, conversión y afiliación (p. ej. Meta Pixel). No se activa hasta configurarla y consentirla.",
      ] },
      { h: "Servicios de terceros", p: "Actualmente WANM no carga cookies de analítica o publicidad de terceros. Cuando dichas integraciones se activen en el futuro, se incluirán en el inventario de cookies a continuación y solo se ejecutarán según tu consentimiento." },
      { h: "Cookies actualmente activas", table: true },
      { h: "Cómo gestionar las cookies", p: "Puedes aceptar o rechazar las cookies no esenciales en cualquier momento mediante el botón de abajo o el enlace \"Preferencias de Cookies\" del pie de página. También puedes controlar las cookies desde la configuración de tu navegador." },
      { h: "Conservación / duración de las cookies", p: "Los periodos de conservación se indican por cookie en el inventario anterior. Tu preferencia de consentimiento se guarda hasta 6 meses, tras lo cual se te preguntará de nuevo." },
      { h: "Actualizaciones de esta Política de Cookies", p: "Podemos actualizar esta política a medida que evolucionen nuestros servicios e integraciones. Los cambios relevantes se reflejarán aquí con una fecha de entrada en vigor revisada." },
      { h: "Contacto", p: "Para consultas sobre privacidad o cookies, contáctanos en:" },
    ],
    invHead: ["Nombre", "Proveedor", "Propósito", "Categoría", "Duración", "Origen"],
  },
};

const CookiePolicy = () => {
  const { lang } = useLang();
  const { openPreferences } = useConsent();
  const c = content[lang] || content.en;
  const catName = (id) => {
    const cat = COOKIE_CATEGORIES.find((x) => x.id === id);
    return cat ? (cat[lang] || cat.en).name : id;
  };
  const contactEmail = orComingSoon(siteConfig.legal.contactEmail || siteConfig.contact.email);

  return (
    <div>
      <PageHero over={c.over} title={c.title} sub={`${c.effective}: ${orComingSoon(siteConfig.legal.effectiveDate)}`} />
      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-3xl px-5 md:px-10">
          <div className="mb-10">
            <Button variant="primary" onClick={openPreferences} testId="policy-manage-btn">{c.manageBtn}</Button>
          </div>
          <div className="space-y-10">
            {c.sections.map((s, i) => (
              <div key={i}>
                <h2 className="text-2xl md:text-3xl font-black text-wanm-dark mb-3">{s.h}</h2>
                {s.p && <p className="text-wanm-dark/70 leading-relaxed text-lg">{s.p}</p>}
                {s.list && (
                  <ul className="mt-3 space-y-2">
                    {s.list.map((li, j) => (
                      <li key={j} className="text-wanm-dark/70 leading-relaxed pl-5 relative">
                        <span className="absolute left-0 top-2.5 w-2 h-2 rounded-full wanm-gradient-bg" />{li}
                      </li>
                    ))}
                  </ul>
                )}
                {s.h === "Contact" || s.h === "Contacto" ? (
                  <p className="mt-2 font-bold text-wanm-red">{contactEmail}</p>
                ) : null}
                {s.table && (
                  <div className="mt-4 overflow-x-auto rounded-2xl border border-wanm-offwhite">
                    <table className="w-full text-left text-sm" data-testid="cookie-inventory">
                      <thead className="bg-wanm-offwhite">
                        <tr>{c.invHead.map((h) => (
                          <th key={h} className="px-4 py-3 font-black text-wanm-dark whitespace-nowrap">{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody>
                        {COOKIE_INVENTORY.filter((x) => x.active).map((ck) => (
                          <tr key={ck.name} className="border-t border-wanm-offwhite">
                            <td className="px-4 py-3 font-mono text-wanm-dark">{ck.name}</td>
                            <td className="px-4 py-3 text-wanm-dark/70">{ck.provider}</td>
                            <td className="px-4 py-3 text-wanm-dark/70">{ck.purpose}</td>
                            <td className="px-4 py-3 text-wanm-dark/70">{catName(ck.category)}</td>
                            <td className="px-4 py-3 text-wanm-dark/70 whitespace-nowrap">{ck.duration}</td>
                            <td className="px-4 py-3 text-wanm-dark/70 whitespace-nowrap">{ck.party}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
            <p className="text-wanm-dark/50 text-sm pt-6 border-t border-wanm-offwhite">
              <Link to="/privacy" className="font-bold text-wanm-red underline underline-offset-2">{lang === "es" ? "Política de Privacidad" : "Privacy Policy"}</Link>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CookiePolicy;
